import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, CheckCircle, Shield, Stethoscope, BadgeDollarSign } from 'lucide-react';
import '../styles/Landing.css';
import Footer from '../components/Footer';
import '../components/UniversalCTA.css';
import SEO from '../components/SEO';
import LuxeCompassWatermark from '../components/LuxeCompassWatermark';
import StatsReel from '../components/StatsReel';

// Health Assessment Modal Component - Embossed Copper Style
const HealthAssessmentModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState(1);
  const [answers, setAnswers] = useState({});

  if (!isOpen) return null;

  const questions = [
    {
      id: 'concern',
      question: "What's your primary health concern?",
      options: [
        "Undiagnosed symptoms",
        "Second opinion on diagnosis",
        "Complex/chronic condition",
        "Navigating specialists",
        "Just exploring options"
      ]
    },
    {
      id: 'timeline',
      question: "How long have you been dealing with this?",
      options: [
        "Less than 3 months",
        "3-12 months",
        "1-3 years",
        "Over 3 years"
      ]
    },
    {
      id: 'frustration',
      question: "What frustrates you most?",
      options: [
        "Feeling dismissed by doctors",
        "No clear diagnosis",
        "Conflicting opinions",
        "Can't get appointments",
        "Don't know what to ask"
      ]
    }
  ];

  const handleAnswer = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
    // Always advance step - completion is triggered when step > questions.length
    setStep(step + 1);
  };

  const handleOpenIntake = () => {
    // Open portal in guest mode with intake tab pre-selected
    window.open('/portal?tab=intake&guest=true', '_blank');
    onClose();
  };

  const currentQuestion = questions[step - 1];
  const isComplete = step > questions.length;

  return (
    <div className="assessment-modal-overlay" onClick={onClose}>
      <div className="assessment-modal-copper" onClick={e => e.stopPropagation()}>
        <button className="assessment-close-copper" onClick={onClose}>
          <X size={24} />
        </button>

        {!isComplete ? (
          <>
            <div className="assessment-progress-copper">
              <div className="progress-dots">
                {questions.map((_, idx) => (
                  <span 
                    key={idx} 
                    className={`progress-dot ${idx < step ? 'filled' : ''} ${idx === step - 1 ? 'active' : ''}`}
                  />
                ))}
              </div>
              <span className="progress-text-copper">Question {step} of {questions.length}</span>
            </div>

            <h2 className="assessment-question-copper">{currentQuestion.question}</h2>

            <div className="assessment-options-copper">
              {currentQuestion.options.map((option, idx) => (
                <button
                  key={idx}
                  className="assessment-option-copper"
                  onClick={() => handleAnswer(currentQuestion.id, option)}
                >
                  <span className="option-text">{option}</span>
                  <span className="option-arrow">→</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <div className="assessment-complete-copper">
            <div className="complete-icon-copper">
              <CheckCircle size={56} />
            </div>
            <h2 className="complete-title-copper">We Can Help.</h2>
            <p className="complete-message-copper">
              Based on your answers, CADENCE can provide the clarity and advocacy you&nbsp;need.
            </p>
            
            <div className="complete-actions-copper">
              <button onClick={handleOpenIntake} className="assessment-cta-primary-copper">
                <span>Continue to Full Health Intake</span>
                <ArrowRight size={20} />
              </button>
              <p className="new-tab-note">Opens in new tab • Your progress here is saved</p>
            </div>

            <p className="complete-note-copper">
              Your information is confidential and HIPAA-protected.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// PAGE 1: LANDING (Hook + Proof)
const Landing = () => {
  const [showAssessment, setShowAssessment] = useState(false);
  // Stats reel handles its own state internally; the old tap-to-expand
  // state was retired in favor of the auto-advancing cinematic reel
  // (founder feedback 2026-02).

  // Cinematic reel — 8 founder-designed slides + closing CTA with logo backdrop.
  // (Founder spec 2026-02: replace text/data slides with custom-designed
  //  artwork that carries the entire narrative visually.)
  const soundFamiliar = {
    image: '/stats-reel/slide-0.png',
  };

  const urgencyStats = [
    {
      id: 'slide-1',
      image: '/stats-reel/slide-1.png',
      alt: 'Slide 1',
    },
    {
      id: 'slide-2',
      image: '/stats-reel/slide-2.png',
      alt: 'Slide 2',
    },
    {
      id: 'slide-3',
      image: '/stats-reel/slide-3.png',
      alt: 'Slide 3',
    },
    {
      id: 'slide-4',
      image: '/stats-reel/slide-4.png',
      alt: 'Slide 4',
    },
    {
      id: 'slide-5',
      image: '/stats-reel/slide-5.png',
      alt: 'Slide 5',
    },
    {
      id: 'slide-6',
      image: '/stats-reel/slide-6.png',
      alt: 'Slide 6',
      dwellMs: 4500,
    },
    {
      id: 'slide-7',
      image: '/stats-reel/slide-7.png',
      alt: 'Slide 7',
      dwellMs: 4500,
    },
  ];

  // Closing slide of the stats reel = the "WE PUT THE PIECES TOGETHER"
  // puzzle visual (closing-option-d.png). No button here — the primary
  // CTA lives in its own section below the CADENCE Promise band.
  const fillsGapCtas = [
    {
      demoKey: 'closing',
      image: '/stats-reel/closing-option-d.png',
      transparent: true,
    },
  ];

  return (
    <div className="home-page">
      <SEO
        title="Medical Expert Opinion & Medical Record Review"
        description="CADENCE Medical Consulting provides medical expert opinions and comprehensive medical record review for complex diagnoses. Veteran-owned, woman-led patient advocacy for those dismissed by doctors."
        path="/home"
      />
      {/* Health Assessment Modal */}
      <HealthAssessmentModal 
        isOpen={showAssessment} 
        onClose={() => setShowAssessment(false)} 
      />

      {/* 1. LARGE VELVET EMBLEM - TOP OF PAGE */}
      <section className="emblem-section" data-testid="home-emblem">
        <div className="container">
          <div className="emblem-wrapper">
            <img 
              src="/cadence-gold-foil.png" 
              alt="CADENCE Medical Consulting" 
              className="velvet-emblem-home" 
            />
          </div>
        </div>
      </section>

      {/* 2. CADENCE EXPERT OPINION TITLE */}
      <section className="unified-content-section" data-testid="cadence-title-home">
        <div className="container">
          <LuxeCompassWatermark />
          <h1 className="unified-page-title">
            CADENCE Medical<br />Expert Opinion
          </h1>
        </div>
      </section>
      
      <section className="unified-quote-section">
        <div className="container">
          <p className="unified-page-quote" style={{ fontStyle: 'italic' }}>
            Gain Clarity & Confidence in Your Diagnosis
          </p>
        </div>
      </section>

      {/* 2c. CADENCE ACRONYM RIBBON — same chassis as the bottom-of-
            page acronym on /coming-soon. */}
      <div className="acronym-tile" data-testid="home-acronym-ribbon">
        <p className="acronym-footer acronym-desktop">
          CLINICAL ADVOCACY • DECISIVE EMPOWERMENT • NAVIGATING CARE & EXPERTISE
        </p>
        <p className="acronym-footer acronym-mobile">
          <span className="acronym-line">CLINICAL ADVOCACY</span>
          <span className="acronym-line">DECISIVE EMPOWERMENT</span>
          <span className="acronym-line">NAVIGATING CARE & EXPERTISE</span>
        </p>
      </div>

      {/* 3. DOCTOR-PATIENT PHOTO */}
      <section className="hero-photo-section" data-testid="hero-photo">
        <div className="container">
          <div className="hero-photo-wrapper">
            <img 
              src="/stats-reel/hero-doctor-patient.jpg" 
              alt="Doctor consulting with patient - compassionate healthcare" 
              className="hero-photo-image" 
            />
          </div>
        </div>
      </section>

      {/* 4. THE PROBLEM — Heritage 5W's plaque chassis acts as the
            MARQUEE HEADER for the Stats Reel below. Founder spec
            2026-02-08: "make this look like the 5W's journey plaque,
            saved as the canonical pattern for future references." */}
      <section className="the-problem-section" data-testid="the-problem">
        <div className="container">
          <div className="problem-plaque-wrap">
            <div className="story-fivew-plaque problem-plaque" aria-label="The Problem We're Solving">
              <div className="story-fivew-plaque-inner">
                <h2 className="story-fivew-title problem-plaque-title">The Problem We&rsquo;re Solving</h2>
                <span className="problem-plaque-subline">Rushed. Dismissed. Unheard. Unresolved.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. PAIN-POINT CINEMATIC CASCADE (founder spec 2026-02):
            FEEL FAMILIAR? bullets fade in
              → 2.5s pause
              → 5 tiles pop in sequentially (0.7s apart)
              → 1s pause
              → "CADENCE fills that gap" solution CTA fades in
          Triggered once when the section scrolls into view. */}
      <section className="urgency-stats-section" data-testid="urgency-stats">
        <div className="container">
          <StatsReel
            soundFamiliar={soundFamiliar}
            stats={urgencyStats}
            ctas={fillsGapCtas}
            testIdPrefix="home-stats-cascade"
          />
        </div>
      </section>

      {/* The "Doctors want to help. / 15 minutes won't let them."
          standalone section was MOVED INTO the cascade CTA tile
          (2026-02 founder feedback "insert that line inside the
          gap CTA, not as separate copy on the velvet"). */}

      {/* "Why CADENCE" trio (Veteran-Owned / 33+ Years / Cash-Pay) was
          relocated to the Founders page above "Our Leadership" per user
          2026-02. Founder context is the natural home for credibility
          trust marks — home page now flows clarity-tile → final CTA. */}

      {/* 7. THE CADENCE PROMISE — the vow lands beneath the stats as the
          emotional payoff (our answer) BEFORE the CTA. A distinct velvet
          plaque; the puzzle/buckle visuals stay unique to the dedicated
          CADENCE Promise page (linked below). */}
      <section className="home-promise-section" data-testid="home-promise">
        <div className="container">
          <div className="home-promise-band">
            <div className="home-promise-inner">
              <h2 className="home-promise-title" data-testid="home-promise-title">The CADENCE Promise</h2>
              <p className="home-promise-vow">
                We will never rush your care, dismiss your concerns, or skim your records.
              </p>
              <p className="home-promise-verbs">
                <span className="home-promise-verb">We&nbsp;Listen.</span>{' '}
                <span className="home-promise-verb">We&nbsp;Investigate.</span>{' '}
                <span className="home-promise-verb">We&nbsp;Translate.</span>{' '}
                <span className="home-promise-verb">We&nbsp;Advocate.</span>
              </p>
              <Link
                to="/cadence-promise"
                className="home-promise-link"
                data-testid="home-promise-link"
              >
                See the full CADENCE Promise <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 8. FINAL CTA — "Find What Others Missed" gold plaque
          (2026-06: restyled from the slim gold pill into the shared
          gold CTA tile — Founders-box shape, foil-gold coloring, gold
          rim; matches UniversalCTA on the other public pages). */}
      <section className="slim-cta-section" data-testid="final-cta">
        <div className="container">
          <div className="universal-cta-tile" data-testid="universal-cta-tile">
            <p className="universal-cta-heading">The Care You Deserve Starts Here</p>
            <Link
              to="/portal?guest=true&tab=intake"
              className="universal-cta-btn"
              data-testid="find-what-others-missed-btn"
            >
              Find What Others Missed
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Landing;
